/**
 * Grid Component
 *
 * A flexible CSS Grid-based layout component for creating
 * responsive grid layouts with consistent spacing.
 *
 * Uses CSS custom properties (design tokens) for gap values,
 * ensuring consistency across the design system.
 *
 * @module components/core/Grid
 *
 * @example
 * ```tsx
 * // Simple 3-column grid
 * <Grid cols={3} gap="md">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 *   <Card>Item 3</Card>
 * </Grid>
 *
 * // Responsive grid with breakpoint-specific columns
 * <Grid cols={1} colsMd={2} colsLg={4} gap="lg">
 *   {products.map(product => <ProductCard key={product.id} />)}
 * </Grid>
 *
 * // Auto-fit grid with minimum child width
 * <Grid cols="auto-fit" minChildWidth="250px" gap="md">
 *   {items.map(item => <Card key={item.id} />)}
 * </Grid>
 *
 * // Grid with different row and column gaps
 * <Grid cols={3} rowGap="lg" colGap="md">
 *   <GridItem>Content</GridItem>
 * </Grid>
 * ```
 */

import { type ElementType, type ReactNode, type CSSProperties, forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Column count options
 */
export type GridCols = 1 | 2 | 3 | 4 | 5 | 6 | 12 | 'auto-fit' | 'auto-fill';

/**
 * Gap size options mapped to CSS custom properties
 */
export type GridGap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Props for the Grid component
 */
export interface GridProps {
  /**
   * Child elements to render in the grid
   */
  children: ReactNode;

  /**
   * Number of columns (or auto-fit/auto-fill for responsive grids)
   * @default 1
   */
  cols?: GridCols;

  /**
   * Number of columns at small breakpoint (640px+)
   */
  colsSm?: number;

  /**
   * Number of columns at medium breakpoint (768px+)
   */
  colsMd?: number;

  /**
   * Number of columns at large breakpoint (1024px+)
   */
  colsLg?: number;

  /**
   * Number of columns at extra-large breakpoint (1280px+)
   */
  colsXl?: number;

  /**
   * Gap between grid items - uses design system spacing tokens
   * Applies to both row and column gaps unless rowGap/colGap are specified
   * @default 'md'
   */
  gap?: GridGap;

  /**
   * Gap between rows - overrides gap for row spacing
   * Can be a token key or a custom CSS value
   */
  rowGap?: string;

  /**
   * Gap between columns - overrides gap for column spacing
   * Can be a token key or a custom CSS value
   */
  colGap?: string;

  /**
   * Minimum width for children when using auto-fit/auto-fill
   * @example '250px', '15rem'
   */
  minChildWidth?: string;

  /**
   * Maximum width for children when using auto-fit/auto-fill
   * @default '1fr'
   */
  maxChildWidth?: string;

  /**
   * The HTML element or React component to render as
   * @default 'div'
   */
  as?: ElementType;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Inline styles (use sparingly - prefer tokens)
   */
  style?: CSSProperties;

  /**
   * Align items along the block (column) axis
   */
  alignItems?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';

  /**
   * Justify items along the inline (row) axis
   */
  justifyItems?: 'start' | 'center' | 'end' | 'stretch';

  /**
   * Align the grid within its container (block axis)
   */
  alignContent?: 'start' | 'center' | 'end' | 'stretch' | 'between' | 'around' | 'evenly';

  /**
   * Justify the grid within its container (inline axis)
   */
  justifyContent?: 'start' | 'center' | 'end' | 'stretch' | 'between' | 'around' | 'evenly';

  /**
   * Whether the grid should take full width
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string;
}

/**
 * Map gap sizes to CSS custom property values with fallbacks
 * Fallbacks ensure gaps work even if CSS variables aren't loaded yet
 */
const gapMap: Record<GridGap, string> = {
  none: '0',
  xs: 'var(--spacing-xs, 0.25rem)',
  sm: 'var(--spacing-sm, 0.5rem)',
  md: 'var(--spacing-md, 1rem)',
  lg: 'var(--spacing-lg, 1.5rem)',
  xl: 'var(--spacing-xl, 2rem)',
  '2xl': 'var(--spacing-2xl, 3rem)',
};

/**
 * Map gap sizes to Tailwind classes (fallback for inline styles)
 */
const gapClassMap: Record<GridGap, string> = {
  none: 'gap-0',
  xs: 'gap-token-xs',
  sm: 'gap-token-sm',
  md: 'gap-token-md',
  lg: 'gap-token-lg',
  xl: 'gap-token-xl',
  '2xl': 'gap-token-2xl',
};

/**
 * Check if a value is a gap token key
 */
function isGapToken(value: string): value is GridGap {
  return value in gapMap;
}

/**
 * Get gap CSS value from token or custom value
 */
function getGapValue(gap: string): string {
  if (isGapToken(gap)) {
    return gapMap[gap];
  }
  return gap;
}

/**
 * Map column count to Tailwind classes
 */
const colsMap: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  12: 'grid-cols-12',
};

/**
 * Map column count to Tailwind sm: breakpoint classes
 */
const colsSmMap: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  5: 'sm:grid-cols-5',
  6: 'sm:grid-cols-6',
  12: 'sm:grid-cols-12',
};

/**
 * Map column count to Tailwind md: breakpoint classes
 */
const colsMdMap: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
  12: 'md:grid-cols-12',
};

/**
 * Map column count to Tailwind lg: breakpoint classes
 */
const colsLgMap: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
  12: 'lg:grid-cols-12',
};

/**
 * Map column count to Tailwind xl: breakpoint classes
 */
const colsXlMap: Record<number, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
  12: 'xl:grid-cols-12',
};

/**
 * Map align-items values to Tailwind classes
 */
const alignItemsMap: Record<string, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

/**
 * Map justify-items values to Tailwind classes
 */
const justifyItemsMap: Record<string, string> = {
  start: 'justify-items-start',
  center: 'justify-items-center',
  end: 'justify-items-end',
  stretch: 'justify-items-stretch',
};

/**
 * Map align-content values to Tailwind classes
 */
const alignContentMap: Record<string, string> = {
  start: 'content-start',
  center: 'content-center',
  end: 'content-end',
  stretch: 'content-stretch',
  between: 'content-between',
  around: 'content-around',
  evenly: 'content-evenly',
};

/**
 * Map justify-content values to Tailwind classes
 */
const justifyContentMap: Record<string, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  stretch: 'justify-stretch',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

/**
 * Grid Component
 *
 * A polymorphic CSS Grid layout component that provides consistent
 * spacing and responsive column layouts using design system tokens.
 */
export const Grid = forwardRef<HTMLElement, GridProps>(function Grid(
  {
    children,
    cols = 1,
    colsSm,
    colsMd,
    colsLg,
    colsXl,
    gap = 'md',
    rowGap,
    colGap,
    minChildWidth,
    maxChildWidth = '1fr',
    as: Component = 'div',
    className,
    style,
    alignItems,
    justifyItems,
    alignContent,
    justifyContent,
    fullWidth = false,
    'data-testid': testId,
    ...restProps
  },
  ref
) {
  // Determine if we need custom grid-template-columns for auto-fit/fill
  const isAutoGrid = cols === 'auto-fit' || cols === 'auto-fill';

  // Build grid-template-columns for auto grids
  const gridTemplateColumns =
    isAutoGrid && minChildWidth ? `repeat(${cols}, minmax(${minChildWidth}, ${maxChildWidth}))` : undefined;

  // Build style object
  const combinedStyle: CSSProperties = {
    ...(gridTemplateColumns && { gridTemplateColumns }),
    gap: rowGap || colGap ? undefined : getGapValue(gap),
    rowGap: rowGap ? getGapValue(rowGap) : undefined,
    columnGap: colGap ? getGapValue(colGap) : undefined,
    ...style,
  };

  // Get column classes (only for numeric cols)
  const colsClass = typeof cols === 'number' ? colsMap[cols] : undefined;
  const colsSmClass = colsSm ? colsSmMap[colsSm] : undefined;
  const colsMdClass = colsMd ? colsMdMap[colsMd] : undefined;
  const colsLgClass = colsLg ? colsLgMap[colsLg] : undefined;
  const colsXlClass = colsXl ? colsXlMap[colsXl] : undefined;

  // Get gap class for Tailwind fallback (only for token gaps without separate row/col)
  const gapClass = !rowGap && !colGap && isGapToken(gap) ? gapClassMap[gap] : undefined;

  return (
    <Component
      ref={ref}
      className={cn(
        'grid',
        colsClass,
        colsSmClass,
        colsMdClass,
        colsLgClass,
        colsXlClass,
        gapClass,
        alignItems && alignItemsMap[alignItems],
        justifyItems && justifyItemsMap[justifyItems],
        alignContent && alignContentMap[alignContent],
        justifyContent && justifyContentMap[justifyContent],
        fullWidth && 'w-full',
        className
      )}
      style={combinedStyle}
      data-testid={testId}
      {...restProps}
    >
      {children}
    </Component>
  );
});

Grid.displayName = 'Grid';

/**
 * Props for the GridItem component
 */
export interface GridItemProps {
  /**
   * Child elements
   */
  children: ReactNode;

  /**
   * Column span
   * @example 2, 'full'
   */
  colSpan?: number | 'full';

  /**
   * Row span
   */
  rowSpan?: number;

  /**
   * Column start position
   */
  colStart?: number;

  /**
   * Column end position
   */
  colEnd?: number;

  /**
   * Row start position
   */
  rowStart?: number;

  /**
   * Row end position
   */
  rowEnd?: number;

  /**
   * The HTML element or React component to render as
   * @default 'div'
   */
  as?: ElementType;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Inline styles
   */
  style?: CSSProperties;

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string;
}

/**
 * Map column span to Tailwind classes
 */
const colSpanMap: Record<number | 'full', string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
  full: 'col-span-full',
};

/**
 * Map row span to Tailwind classes
 */
const rowSpanMap: Record<number, string> = {
  1: 'row-span-1',
  2: 'row-span-2',
  3: 'row-span-3',
  4: 'row-span-4',
  5: 'row-span-5',
  6: 'row-span-6',
};

/**
 * GridItem Component
 *
 * A component for positioning items within a Grid.
 * Allows spanning multiple columns/rows and precise positioning.
 */
export const GridItem = forwardRef<HTMLElement, GridItemProps>(function GridItem(
  {
    children,
    colSpan,
    rowSpan,
    colStart,
    colEnd,
    rowStart,
    rowEnd,
    as: Component = 'div',
    className,
    style,
    'data-testid': testId,
    ...restProps
  },
  ref
) {
  // Build style for positioning that Tailwind doesn't cover directly
  const combinedStyle: CSSProperties = {
    ...(colStart && { gridColumnStart: colStart }),
    ...(colEnd && { gridColumnEnd: colEnd }),
    ...(rowStart && { gridRowStart: rowStart }),
    ...(rowEnd && { gridRowEnd: rowEnd }),
    ...style,
  };

  const colSpanClass = colSpan !== undefined ? colSpanMap[colSpan] : undefined;
  const rowSpanClass = rowSpan !== undefined ? rowSpanMap[rowSpan] : undefined;

  return (
    <Component
      ref={ref}
      className={cn(colSpanClass, rowSpanClass, className)}
      style={combinedStyle}
      data-testid={testId}
      {...restProps}
    >
      {children}
    </Component>
  );
});

GridItem.displayName = 'GridItem';

export default Grid;
